import { LessonHookResponseDto } from "@packages/api/madlenAPI.schemas";
import CardImage from "../tool_components/CardImage";
import { RefreshCw, Sparkles } from "lucide-react";
import { tC } from "../../utils/TranslateWithContentLanguage";
import HookCardPDF from "./HookCardPdf";
import PdfDownloadButton from "../pdf/PdfDownloadButton";
import useImagePdf from "../pdf/useImagePdf";
import { Button } from "@core/index";
import { useAppSelector } from "@packages/store/hooks";
import { useState } from "react";
import PdfCustomizationModal from "@features/common/components/pdf-customization-modal";
import EditableMarkdown from "../tool_components/EditableMarkdown";
import { useQueryClient } from "@tanstack/react-query";
import {
  getGetLessonHookQueryKey,
  getGetUserLessonHooksQueryKey,
  updateLessonHook,
} from "@packages/api/madlenAPI";

export const HookCard = ({
  lessonHook,
  isImageLoading,
  onCreateAgain,
  isEditable,
}: {
  lessonHook: LessonHookResponseDto;
  isImageLoading: boolean;
  onCreateAgain?: () => void;
  isEditable?: boolean;
}) => {
  const finalImageUrl = useImagePdf(lessonHook.imageUrl);
  const [showPdfCustomizationModal, setShowPdfCustomizationModal] =
    useState(false);
  const customizationSettings = useAppSelector(
    (state) => state.user.teacher?.preferences?.pdfOptions,
  );
  const queryClient = useQueryClient();
  const contentLanguage = lessonHook.contentLanguage || "tr";

  return (
    <div className="space-y-6">
      <CardImage
        createdAt={lessonHook.createdAt}
        theme="blue"
        imageUrl={lessonHook.imageUrl}
        videoUrl={lessonHook.videoUrl}
        isLoading={isImageLoading}
        courseName={lessonHook.courseName}
        gradeLevel={lessonHook.gradeLevel}
        topic={lessonHook.title || lessonHook.prompt}
        contentLanguage={contentLanguage}
      />
      <div className="flex justify-end gap-2 mt-2">
        {onCreateAgain && (
          <Button
            onClick={onCreateAgain}
            variant="solid"
            size="md"
            textColor="text-white dark:text-white"
            bgColor="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
            rounded="xl"
          >
            <RefreshCw size={18} />
            <span>{tC(contentLanguage, "common.createAnother")}</span>
          </Button>
        )}
        <PdfDownloadButton
          openCustomizationModal={() => setShowPdfCustomizationModal(true)}
          pdfTitle={lessonHook.title || ""}
          document={
            <HookCardPDF
              customizationSettings={customizationSettings}
              lessonHook={{
                ...lessonHook,
                imageUrl: finalImageUrl,
              }}
            />
          }
          btnTheme="bg-gradient-to-r from-blue-700 to-blue-500"
        />
      </div>

      <div className="space-y-4">
        <div className="grid gap-4">
          {Object.entries((lessonHook.hooks as Record<string, any>) || {}).map(
            ([hookKey, hook], index) => (
              <div key={index} className="space-y-4">
                {hook.title && (
                  <div className="flex flex-row items-center gap-2 text-blue-600 dark:text-blue-500 rounded-xl p-4">
                    <span className="p-2 rounded-xl bg-blue-500 dark:bg-blue-900/75 text-blue-500 dark:text-blue-500">
                      <Sparkles
                        size={18}
                        className="text-blue-50 dark:text-blue-100"
                      />
                    </span>
                    <h5 className="font-semibold text-lg">{hook.title}</h5>
                  </div>
                )}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 space-y-4">
                  <div className="max-w-none mt-2">
                    <div className="leading-relaxed">
                      <EditableMarkdown
                        value={hook.description || hook.content}
                        isEditable={isEditable ?? true}
                        handleSave={async (newValue) => {
                          const updatedHooks = { ...lessonHook.hooks };
                          updatedHooks[hookKey] = {
                            ...updatedHooks[hookKey],
                            description: newValue,
                            content: newValue,
                          };

                          const hooksArray = Object.entries(updatedHooks).map(
                            ([key, value]) => ({
                              ...value,
                              key,
                            }),
                          );

                          await updateLessonHook(lessonHook.id, {
                            hooks: hooksArray,
                          });

                          await queryClient.invalidateQueries({
                            queryKey: getGetUserLessonHooksQueryKey(),
                          });
                          await queryClient.invalidateQueries({
                            queryKey: getGetLessonHookQueryKey(lessonHook.id),
                          });
                        }}
                      />
                    </div>
                  </div>
                  {hook.expected_outcome && (
                    <div className="mt-4">
                      <h6 className="text-sm font-bold text-gray-800 dark:text-gray-200">
                        {tC(contentLanguage, "hooks.expectedOutcome")}:
                      </h6>
                      <div className="mt-1">
                        <EditableMarkdown
                          value={hook.expected_outcome}
                          isEditable={isEditable ?? true}
                          handleSave={async (newValue) => {
                            const updatedHooks = { ...lessonHook.hooks };
                            updatedHooks[hookKey] = {
                              ...updatedHooks[hookKey],
                              expected_outcome: newValue,
                            };

                            const hooksArray = Object.entries(updatedHooks).map(
                              ([key, value]) => ({
                                ...value,
                                key,
                              }),
                            );

                            await updateLessonHook(lessonHook.id, {
                              hooks: hooksArray,
                            });

                            await queryClient.invalidateQueries({
                              queryKey: getGetUserLessonHooksQueryKey(),
                            });
                            await queryClient.invalidateQueries({
                              queryKey: getGetLessonHookQueryKey(lessonHook.id),
                            });
                          }}
                        />
                      </div>
                    </div>
                  )}
                  {hook.materials_needed &&
                    hook.materials_needed !== "Yok" &&
                    hook.materials_needed !== "None" &&
                    hook.materials_needed !== "None." && (
                      <div className="mt-4">
                        <h6 className="text-sm font-bold text-gray-800 dark:text-gray-200">
                          {tC(contentLanguage, "hooks.materialsNeeded")}:
                        </h6>
                        <div className="mt-1 text-sm">
                          <EditableMarkdown
                            value={hook.materials_needed}
                            isEditable={isEditable ?? true}
                            handleSave={async (newValue) => {
                              const updatedHooks = { ...lessonHook.hooks };
                              updatedHooks[hookKey] = {
                                ...updatedHooks[hookKey],
                                materials_needed: newValue,
                              };

                              const hooksArray = Object.entries(
                                updatedHooks,
                              ).map(([key, value]) => ({
                                ...value,
                                key,
                              }));

                              await updateLessonHook(lessonHook.id, {
                                hooks: hooksArray,
                              });

                              await queryClient.invalidateQueries({
                                queryKey: getGetUserLessonHooksQueryKey(),
                              });
                              await queryClient.invalidateQueries({
                                queryKey: getGetLessonHookQueryKey(
                                  lessonHook.id,
                                ),
                              });
                            }}
                          />
                        </div>
                      </div>
                    )}
                </div>
              </div>
            ),
          )}
        </div>
      </div>
      {/* PDF Customization Modal */}
      <PdfCustomizationModal
        show={showPdfCustomizationModal}
        onClose={() => setShowPdfCustomizationModal(false)}
        hideSchoolLogoOptions
        documentComponent={
          <HookCardPDF
            customizationSettings={customizationSettings}
            lessonHook={{
              ...lessonHook,
              imageUrl: finalImageUrl,
            }}
          />
        }
      />
    </div>
  );
};
